import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useThree } from '@react-three/fiber'
import { Frame } from './Frame'
import * as THREE from 'three'
import { DoubleSide, Vector2, SRGBColorSpace } from 'three'
import { useInteractionManager } from '../../hooks/useWebCrafter.jsx'
import { debugLog, debugError } from '../../utils/debug'

export function Video(props)
{
  const { src, size = 1, framed, muted, volume = 1, frameMaterial, frameWidth = 1, sourceObject = null, ...rest } = props

  const camera = useThree(state => state.camera)
  const interactionManager = useInteractionManager()

  const [ speaker, setSpeaker ] = useState()
  const [ dims, setDims ] = useState()
  const [ callbacks, setCallbacks ] = useState({})


  const videoRef = useRef(document.createElement('video'))

  // Create and configure video element
  const video = useMemo(() =>
  {
    if (!src) return null
    if (videoRef.current?.src === src) return videoRef.current

    const v = videoRef.current
    if (!v) return null

    v.playsInline = true
    v.crossOrigin = 'anonymous'
    v.loop = true
    v.src = src
    v.autoplay = false
    v.muted = muted ?? false
    v.volume = volume

    v.addEventListener('loadeddata', () =>
    {
      setDims(new Vector2(v.videoWidth, v.videoHeight))
    })

    return v
  }, [ src, videoRef.current ])

  const toggleVideo = useCallback(() =>
  {
    if (!video) return

    if (video.paused)
    {
      if (speaker?.context?.state === 'suspended')
      {
        speaker.context.resume()
      }
      video.play().catch(err => debugError('[Video] Playback failed:', err))
    } else
    {
      video.pause()
    }
  }, [ video, speaker ])

  // Subscribe to interaction manager video triggers
  useEffect(() =>
  {
    if (!interactionManager) return

    const handleTrigger = (name) =>
    {
      if (sourceObject && name === sourceObject.name)
      {
        toggleVideo()
      }
    }

    const unsubscribe = interactionManager.subscribeToVideoTrigger(handleTrigger)
    return () => unsubscribe()
  }, [ sourceObject, toggleVideo, interactionManager ])

  useEffect(() =>
  {
    if (!speaker) return

    speaker.setVolume(volume)
  }, [ volume, speaker ])

  // Setup audio and interaction callbacks
  useEffect(() =>
  {
    if (!video) return

    // Setup audio
    if (!muted && !video.paused && !speaker)
    {
      const listener = camera.children.find(c => c.type === 'AudioListener')
      if (listener)
      {
        const speak = new THREE.Audio(listener)
        speak.setMediaElementSource(video)
        speak.setVolume(volume)
        setSpeaker(speak)
      }
    }

    // Setup interaction callbacks based on trigger type
    const mediaTrigger = sourceObject?.userData?.mediaTrigger
    const tempCallbacks = {}

    if (mediaTrigger === 'Looping')
    {
      video.play().catch(e => debugError('[Video] Auto-play failed:', e))
    } else if (mediaTrigger === 'OnPointerExit' || mediaTrigger === 'OnPointerEnter')
    {
      tempCallbacks.onPointerEnter = (e) =>
      {
        e?.stopPropagation()
        video.play().catch(err => debugError('[Video] Play failed:', err))
      }
      tempCallbacks.onPointerLeave = (e) =>
      {
        e?.stopPropagation()
        video.pause()
      }
    } else
    {
      // Default: click to toggle
      tempCallbacks.onClick = (e) =>
      {
        e?.stopPropagation()
        toggleVideo()
      }
    }

    setCallbacks(tempCallbacks)
  }, [ speaker, video, muted, camera, volume, sourceObject, toggleVideo ])


  // Cleanup on unmount
  useEffect(() =>
  {
    return () =>
    {
      if (speaker)
      {
        if (speaker.isPlaying) speaker.stop()
        speaker.disconnect()
        setSpeaker(undefined)
      }
      if (video)
      {
        video.pause()
        video.remove()
      }
    }
  }, [])

  if (!dims || !video)
  {
    return null
  }

  // Calculate dimensions from sourceObject's world bounding box
  let width = 1
  let height = 1
  let scale = 1
  const rotation = [ Math.PI, Math.PI, Math.PI ]

  if (sourceObject)
  {
    const box = new THREE.Box3().setFromObject(sourceObject)
    const boxSize = new THREE.Vector3()
    box.getSize(boxSize)

    width = boxSize.x
    height = (dims.y / dims.x) * boxSize.x

    const sortedDims = [ boxSize.x, boxSize.y, boxSize.z ].sort((a, b) => b - a)
    scale = sortedDims[ 0 ] / width
  }

  return (
    <group name={'webcrafter-video' + src} {...rest}>
      <mesh {...callbacks} rotation={rotation} scale={[ scale, scale, scale ]}>
        <planeGeometry args={[ width, height ]} />
        <meshBasicMaterial side={DoubleSide}>
          <videoTexture attach='map' args={[ video ]} colorSpace={SRGBColorSpace} />
        </meshBasicMaterial>
      </mesh>
      {framed && <Frame width={width} height={height} thickness={frameWidth} material={frameMaterial} rotation={rotation} />}
    </group>
  )
}
